import React, { useEffect, useState, useRef } from "react";
import {
  FileText, Upload, Loader2, BookOpen, Quote, Library,
  ScrollText, Trash2, FilePlus, Hash, ScanText, ListPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { pdfs as pdfsApi, bibliography, normalizeError } from "@/lib/api";
import { useLLM } from "@/contexts/LLMContext";
import BibliographyPanel, { originMeta } from "@/components/BibliographyPanel";
import { toast } from "sonner";
import NumaLogo from "@/components/NumaLogo";
import { sanitizeHtml } from "@/lib/sanitize";
import AiBadge from "@/components/AiBadge";

/**
 * Reads the bibliography *of* a source, as opposed to the reference *to* it.
 *
 * Marked as AI because it is: splitting a free-text reference into author,
 * title and year is a reading task, and the regular expressions that used to
 * attempt it produced citations that looked right and were not.
 */
function BibliographyOfFileButton({ pdf, busy, onClick, inline = false }) {
  return (
    <button
      type="button"
      data-testid={`extract-bibliography-${pdf.pdf_id}`}
      onClick={onClick}
      disabled={busy}
      title="Legge la sezione bibliografica del documento e la aggiunge alla tua bibliografia"
      className={`inline-flex items-center gap-1.5 rounded-md border border-line-strong px-2.5 py-1 text-xs text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-60 ${inline ? "" : "mt-0.5"}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListPlus className="w-3 h-3" />}
      Estrai la bibliografia del file
      <span className="ml-1 rounded bg-assistant-soft px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-assistant">
        AI
      </span>
    </button>
  );
}

function PageBadge({ children, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-assistant-soft text-assistant px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
    >
      <Hash className="w-2.5 h-2.5" />
      {children}
    </span>
  );
}

function VerseBadge({ verse }) {
  if (!verse) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] bg-warn/10 text-warn px-2 py-0.5 rounded-full font-medium italic">
      <ScrollText className="w-2.5 h-2.5" />
      {verse}
    </span>
  );
}

function QuoteBlock({ quote, verse }) {
  if (!quote) return null;
  return (
    <blockquote className="mt-3 pl-3 border-l-[3px] border-brand bg-paper rounded-r-lg py-2 pr-3">
      <p className="text-xs italic text-ink-muted leading-relaxed">
        <span className="text-assistant font-serif text-base align-middle mr-1">&laquo;</span>
        {quote}
        <span className="text-assistant font-serif text-base align-middle ml-1">&raquo;</span>
      </p>
      {verse && <div className="mt-1.5"><VerseBadge verse={verse} /></div>}
    </blockquote>
  );
}

export default function PDFLibrary({ thesis, onListChange }) {
  const { ensureKey, reportError } = useLLM();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [openSummary, setOpenSummary] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const [pageViewer, setPageViewer] = useState({ open: false, pdf: null, page: 1, pages: [], loading: false });
  const [qaState, setQaState] = useState({ loading: false, question: "", answer: null });
  const [searchQuery, setSearchQuery] = useState("");
  // The reference extracted from each file, keyed by document, so the result of
  // an upload is visible next to the file it came from.
  const [refByPdf, setRefByPdf] = useState({});
  const [extractingId, setExtractingId] = useState(null);
  const [listingId, setListingId] = useState(null);
  const [ocrId, setOcrId] = useState(null);
  // The file picker used to hand a scanned source back for transcription.
  const ocrInputRef = useRef(null);
  const ocrTargetRef = useRef(null);
  const [bibRefresh, setBibRefresh] = useState(0);
  const bibliographyRef = useRef(null);

  const load = async () => {
    if (!thesis?.thesis_id) return;
    const list = await pdfsApi.list(thesis.thesis_id);
    setItems(list);
    onListChange?.(list);
    try {
      const bib = await bibliography.list(thesis.thesis_id);
      const byPdf = {};
      for (const entry of bib.entries || []) {
        if (entry.pdf_id) byPdf[entry.pdf_id] = entry;
      }
      setRefByPdf(byPdf);
    } catch {
      // The bibliography panel below reports its own failures; the library
      // itself stays usable without them.
    }
  };

  const showBibliography = () =>
    bibliographyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /**
   * Read the bibliographic reference out of one source.
   *
   * Free and exact when the file carries a DOI or usable metadata; only what
   * those miss reaches the model. Run on upload, because a reference nobody
   * asked for is the whole point of having uploaded the file.
   */
  const extractReference = async (pdfId) => {
    setExtractingId(pdfId);
    try {
      const res = await bibliography.enrich(thesis.thesis_id, pdfId);
      await load();
      setBibRefresh((n) => n + 1);
      return res;
    } finally {
      setExtractingId(null);
    }
  };

  /**
   * Read the whole reference list contained in a source.
   *
   * Different from the single reference above: that one answers "what is this
   * document", this one answers "what does it cite", which for a chapter is
   * usually thirty entries and is the reason the file was uploaded at all.
   */
  const extractBibliography = async (pdf) => {
    setListingId(pdf.pdf_id);
    try {
      const res = await bibliography.fromSource(thesis.thesis_id, pdf.pdf_id);
      await load();
      setBibRefresh((n) => n + 1);
      const seeIt = { label: "Vedi", onClick: showBibliography };
      if (!res.added) {
        toast.info("Nessun riferimento riconosciuto in questa fonte", {
          description: res.section_found
            ? "La sezione bibliografica è stata trovata ma non conteneva voci leggibili."
            : "Non è stata trovata una sezione bibliografica nel documento.",
        });
      } else {
        const parts = [];
        if (res.by_crossref) parts.push(`${res.by_crossref} verificati su Crossref`);
        if (res.by_model) parts.push(`${res.by_model} letti dall'AI`);
        toast.success(`${res.added} riferimenti aggiunti`, {
          description: parts.join(" · "),
          action: seeIt,
        });
      }
      if (res.pending) {
        toast.info(`${res.pending} riferimenti richiedono un modello`, {
          description: "Non hanno un DOI: solo l'AI può scomporli in autore, titolo e anno.",
        });
      }
    } catch (e) {
      // The dialog raised by reportError carries the message and the remedy.
      reportError(e);
    } finally {
      setListingId(null);
    }
  };

  /** Transcribe a scan, with the file the author points at again. */
  const runOcr = async (file) => {
    const pdf = ocrTargetRef.current;
    if (!pdf || !file) return;
    setOcrId(pdf.pdf_id);
    try {
      const res = await pdfsApi.ocr(pdf.pdf_id, file);
      await load();
      toast.success(`${res.pages_read} pagine trascritte`, {
        description: res.used_model
          ? "Lette dal modello collegato: verifica i dati prima di citarli."
          : "Lette in locale: il documento non ha lasciato il tuo dispositivo.",
      });
    } catch (e) {
      // The dialog raised by reportError carries the message and the remedy.
      reportError(e);
    } finally {
      setOcrId(null);
      ocrTargetRef.current = null;
    }
  };

  /** Say what was found, and where to look at it. */
  const reportExtraction = (res) => {
    const seeIt = { label: "Vedi", onClick: showBibliography };
    if (res?.resolved) {
      toast.success("Riferimento verificato su Crossref", {
        description: "Lo trovi in Bibliografia, in fondo a questa pagina.",
        action: seeIt,
      });
    } else if (res?.from_metadata) {
      toast.success("Riferimento ricavato dai metadati del file", {
        description: "Lo trovi in Bibliografia, in fondo a questa pagina. Controllalo.",
        action: seeIt,
      });
    } else if (res?.by_model) {
      toast.success("Riferimento letto dal frontespizio", {
        description: "Letto dall'AI: verificalo in Bibliografia, in fondo alla pagina.",
        action: seeIt,
      });
    } else if (res?.needs_model) {
      toast.info("Questa fonte non ha un DOI", {
        description:
          "Collega un modello per farle leggere il frontespizio, oppure aggiungi il riferimento a mano in Bibliografia.",
        action: seeIt,
      });
    } else {
      toast.info("Nessun dato bibliografico nel file", {
        description: "Puoi aggiungere il riferimento a mano in Bibliografia.",
        action: seeIt,
      });
    }
  };

  useEffect(() => { load(); }, [thesis?.thesis_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredItems = items.filter((it) => {
    if (!searchQuery) return true;
    return (it.filename || "").toLowerCase().includes(searchQuery.toLowerCase());
  });

  const onFile = async (f) => {
    if (!f) return;
    if (!/\.(pdf|docx)$/i.test(f.name)) { toast.error("Solo PDF o DOCX"); return; }
    setUploading(true);
    try {
      const uploaded = await pdfsApi.upload(thesis.thesis_id, f);
      toast.success(`${f.name} caricato`);
      await load();

      // Before the summary, and before any key is asked for: the reference is
      // free whenever the file carries a DOI or its own metadata.
      try {
        reportExtraction(await extractReference(uploaded.pdf_id));
      } catch (e) {
        reportError(e);
      }
      // No summary here, and therefore no provider dialog. Uploading is not an
      // AI action; raising the key window the moment a file arrived made the
      // whole section look as though none of it worked without a model. The
      // summary is one click away, on the source itself, which is where asking
      // for a key is understandable.
    } catch (e) {
      reportError(e);
    } finally {
      setUploading(false);
    }
  };

  const openPdfSummary = async (pdf) => {
    setOpenSummary(pdf);
    if (pdf.summary_blocks?.length || pdf.key_points_struct?.length) {
      setSummary(pdf);
      return;
    }
    // Re-fetch in case structured fields exist on backend but were filtered
    try {
      const full = await pdfsApi.get(pdf.pdf_id);
      setSummary(full);
    } catch {
      setSummary(pdf);
    }
  };

  const openPageViewer = async (pdf, pageNum = 1) => {
    setPageViewer((s) => ({ ...s, open: true, loading: true, pdf, page: pageNum }));
    try {
      const full = await pdfsApi.getPages(pdf.pdf_id);
      setPageViewer({ open: true, pdf, page: pageNum, pages: full.pages_text || [], loading: false });
    } catch (e) {
      toast.error("Impossibile caricare le pagine");
      setPageViewer({ open: false, pdf: null, page: 1, pages: [], loading: false });
    }
  };

  const summarize = async (pdf) => {
    if (!(await ensureKey())) return;
    setBusyId(pdf.pdf_id);
    setOpenSummary(pdf);
    setSummary(null);
    try {
      const res = await pdfsApi.summarize(pdf.pdf_id);
      setSummary({ ...pdf, ...res });
      await load();
    } catch (e) {
      // The dialog raised by reportError carries the message and the remedy.
      reportError(e);
      setOpenSummary(null);
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    const p = confirmDelete;
    if (!p) return;
    try {
      await pdfsApi.remove(p.pdf_id);
      toast.success("Fonte eliminata");
      await load();
    } catch { toast.error("Errore eliminazione"); }
    finally { setConfirmDelete(null); }
  };

  const isLiterary = !!summary?.is_literary;
  const blocks = summary?.summary_blocks || [];
  const kpoints = summary?.key_points_struct || [];
  return (
    <div className="h-screen overflow-y-auto bg-surface numa-fade-in" data-testid="pdf-library">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 pt-8 lg:pt-14 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <NumaLogo size={8} className="" />
          <p className="text-xs uppercase tracking-widest text-assistant font-medium">Fonti & Bibliografia</p>
        </div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-1 text-ink">Carica le tue fonti</h1>
        <AiBadge mode="optional" className="mt-3"
          detail="Caricamento, bibliografia e citazioni sono algoritmici. Riassunti e domande sulle fonti usano il modello collegato." />
        <p className="mt-2 max-w-3xl text-ink-muted">
          PDF o DOCX. Di ogni fonte NUMA ricava il riferimento bibliografico e lo mostra
          sotto il file e nella{" "}
          <button
            type="button"
            onClick={showBibliography}
            className="text-brand underline underline-offset-2 hover:text-brand-hover"
          >
            bibliografia
          </button>
          . Riassunti e citazioni per pagina richiedono un modello.
        </p>

        {/* Drop zone */}
        <div
          data-testid="sources-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
          className={`mt-8 cursor-pointer border-2 border-dashed rounded-lg p-8 sm:p-10 text-center transition-all relative overflow-hidden ${
            dragOver ? "border-brand bg-assistant-soft" : "border-line bg-surface hover:bg-surface-2/40"
          }`}
        >
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-assistant-soft/40 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="w-14 h-14 mx-auto rounded-full bg-brand-soft flex items-center justify-center mb-4">
              {uploading ? <Loader2 className="w-6 h-6 text-brand animate-spin" /> : <FilePlus className="w-6 h-6 text-brand" />}
            </div>
            <p className="font-serif-display text-xl font-semibold text-ink">
              {uploading ? "Caricamento in corso..." : "Trascina o carica un PDF o DOCX"}
            </p>
            <p className="text-sm text-ink-muted mt-2 max-w-md mx-auto">
              NUMA leggerà ogni pagina e citerà le fonti con riferimenti precisi.
            </p>
            <Button
              data-testid="upload-pdf-btn"
              type="button"
              disabled={uploading}
              className="mt-5 rounded-md bg-brand hover:bg-brand-hover text-on-solid"
            >
              <Upload className="w-4 h-4 mr-2" /> Scegli un file
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <input
              ref={ocrInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              data-testid="ocr-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                runOcr(file);
              }}
            />
          </div>
        </div>

        {/* Library */}
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <input
              data-testid="sources-search"
              placeholder="Cerca nelle fonti..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 rounded-md border px-3 py-2 text-sm"
            />
            <Button variant="outline" onClick={() => { setSearchQuery(""); load(); }} className="rounded-md">Reset</Button>
          </div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-xl font-semibold text-ink">La tua biblioteca</h2>
            <span className="text-xs text-ink-muted">{items.length} fonti</span>
          </div>
          <div className="space-y-3" data-testid="pdf-list">
            {filteredItems.length === 0 && (
              <div className="bg-surface border border-line rounded-md p-10 text-center">
                <Library className="w-10 h-10 mx-auto text-assistant mb-3" />
                <p className="text-sm text-ink-muted">La tua biblioteca è vuota. Carica una fonte per iniziare.</p>
              </div>
            )}
            {filteredItems.map((p) => {
              const ready = !!(p.summary_blocks?.length || p.key_points_struct?.length || p.summary);
              const reference = refByPdf[p.pdf_id];
              return (
                <div
                  key={p.pdf_id}
                  data-testid={`pdf-${p.pdf_id}`}
                  className="bg-surface border border-line rounded-md p-4 sm:p-5 hover:border-line-strong hover:shadow-sm transition"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-14 rounded-lg bg-surface border border-line flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-assistant" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-ink truncate">{p.filename}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-ink-muted flex-wrap">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${ready ? "bg-ok" : "bg-warn"}`} />
                          {ready ? "Pronto" : p.status}
                        </span>
                        <span>·</span>
                        <span>{p.pages} pagine</span>
                        {ready && (
                          <>
                            <span>·</span>
                            <span className="text-assistant">{(p.key_points_struct?.length || p.key_points?.length || 0)} punti chiave</span>
                          </>
                        )}
                        {p.is_literary && (
                          <>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1 text-warn">
                              <ScrollText className="w-3 h-3" /> testo letterario
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ready ? (
                      <Button
                        data-testid={`view-summary-btn-${p.pdf_id}`}
                        onClick={() => openPdfSummary(p)}
                        className="rounded-md bg-brand hover:bg-brand-hover"
                      >
                        <BookOpen className="w-3.5 h-3.5 mr-2" /> Apri riassunto
                      </Button>
                    ) : (
                      <Button
                        data-testid={`summarize-btn-${p.pdf_id}`}
                        onClick={() => summarize(p)}
                        disabled={busyId === p.pdf_id}
                        variant="outline"
                        className="rounded-md border-line hover:bg-surface-2"
                      >
                        {busyId === p.pdf_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <BookOpen className="w-3.5 h-3.5 mr-2" />}
                        Riassumi
                      </Button>
                    )}
                    <Button
                      data-testid={`delete-pdf-${p.pdf_id}`}
                      onClick={() => setConfirmDelete(p)}
                      variant="ghost"
                      size="icon"
                      className="rounded-md text-ink-subtle hover:text-danger hover:bg-danger/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  </div>

                  {/* A scan has no text layer: every other feature - summary,
                      citations, bibliography - is working on an empty string
                      until this is dealt with, so it is said here rather than
                      left to be discovered downstream. */}
                  {p.needs_ocr && (
                    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-warn/40 bg-warn/5 px-3.5 py-3">
                      <ScanText className="w-4 h-4 shrink-0 text-warn" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-ink">
                          Questa fonte è una scansione
                        </p>
                        <p className="numa-meta mt-0.5">
                          Le pagine sono immagini: senza trascrizione NUMA non può leggerne
                          né il contenuto né la bibliografia.
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid={`ocr-btn-${p.pdf_id}`}
                        onClick={() => { ocrTargetRef.current = p; ocrInputRef.current?.click(); }}
                        disabled={ocrId === p.pdf_id}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-[12.5px] font-medium text-on-solid hover:bg-brand-hover disabled:opacity-60"
                      >
                        {ocrId === p.pdf_id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <ScanText className="w-3.5 h-3.5" />}
                        Leggi con l&apos;OCR
                      </button>
                    </div>
                  )}

                  {/* What the upload actually produced, next to the file it
                      came from. Without this the extraction happens somewhere
                      the author never looks, and reads as not having happened. */}
                  {reference ? (
                    <div
                      data-testid={`pdf-reference-${p.pdf_id}`}
                      className="mt-4 rounded-md border border-line bg-paper px-3.5 py-3"
                    >
                      <div className="flex items-center gap-1.5">
                        <Quote className="w-3 h-3 text-ink-subtle" />
                        <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
                          Riferimento bibliografico
                        </span>
                      </div>
                      <p className="mt-1.5 font-serif-display text-[13.5px] leading-relaxed text-ink">
                        {reference.entry}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px]">
                        <span className={originMeta(reference.origin).tone}>
                          {originMeta(reference.origin).text}
                        </span>
                        <button
                          type="button"
                          onClick={showBibliography}
                          className="text-brand hover:underline"
                        >
                          Vedi in bibliografia
                        </button>
                      </div>
                      {!p.needs_ocr && (
                        <BibliographyOfFileButton
                          pdf={p}
                          busy={listingId === p.pdf_id}
                          onClick={() => extractBibliography(p)}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-3">
                      <span className="text-xs text-ink-subtle">
                        {p.needs_ocr
                          ? "Il riferimento si potrà ricavare dopo la trascrizione."
                          : "Nessun riferimento bibliografico ricavato da questa fonte."}
                      </span>
                      {/* Offering it on a scan would promise something that
                          cannot work: there is no text to read it from yet. */}
                      {!p.needs_ocr && (
                      <button
                        type="button"
                        data-testid={`extract-reference-${p.pdf_id}`}
                        onClick={async () => {
                          try {
                            reportExtraction(await extractReference(p.pdf_id));
                          } catch (e) {
                            reportError(e);
                          }
                        }}
                        disabled={extractingId === p.pdf_id}
                        className="inline-flex items-center gap-1.5 rounded-md border border-line-strong px-2.5 py-1 text-xs text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-60"
                      >
                        {extractingId === p.pdf_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Quote className="w-3 h-3" />
                        )}
                        Estrai il riferimento
                      </button>
                      )}
                      {!p.needs_ocr && (
                        <BibliographyOfFileButton
                          pdf={p}
                          busy={listingId === p.pdf_id}
                          onClick={() => extractBibliography(p)}
                          inline
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}

          </div>
        </div>

        {/*
          Inside the page container, not after it: out here it lost the side
          gutters and the bottom padding, so it ran edge to edge and ended
          flush with the last pixel of the document.
        */}
        <div ref={bibliographyRef} className="mt-14 border-t border-line pt-10 pb-6">
          <BibliographyPanel thesis={thesis} reloadToken={bibRefresh} />
        </div>
      </div>


      {/* Summary dialog with citations */}
      <Dialog open={!!openSummary} onOpenChange={(o) => !o && (setOpenSummary(null), setSummary(null))}>
        <DialogContent data-testid="summary-dialog" className="max-w-[95vw] sm:max-w-3xl rounded-md">
          <DialogHeader>
            <DialogTitle className="font-serif-display flex items-center gap-2">
              <NumaLogo size={7} className="rounded-lg" />
              <span className="truncate">{openSummary?.filename}</span>
              {isLiterary && (
                <span className="text-[10px] bg-warn/10 text-warn px-2 py-0.5 rounded-full font-medium ml-2">
                  Testo letterario
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-4">
            {!summary ? (
              <div className="space-y-3 py-4">
                <div className="numa-shimmer h-4 rounded w-full" />
                <div className="numa-shimmer h-4 rounded w-5/6" />
                <div className="numa-shimmer h-4 rounded w-4/6" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary blocks */}
                {blocks.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <Quote className="w-4 h-4 text-assistant" />
                      <p className="text-[11px] uppercase tracking-widest text-assistant font-medium">Sintesi {isLiterary ? "letteraria" : "del documento"}</p>
                    </div>
                    <div className="space-y-3" data-testid="summary-blocks">
                      {blocks.map((b, i) => (
                        <div key={i} data-testid={`summary-block-${i}`} className="bg-surface border border-line rounded-md p-4">
                          <p className="text-sm leading-relaxed text-ink">{b.text}</p>
                          <QuoteBlock quote={b.quote} verse={b.verse} />
                          {b.pages?.length > 0 && (
                            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] text-ink-subtle uppercase tracking-wider mr-1">Riferimento:</span>
                              {b.pages.map((pg, k) => (
                                <button key={k} className="border-0 bg-transparent p-0" onClick={() => openPageViewer(openSummary || summary, pg)}>
                                  <PageBadge testid={`block-${i}-page-${pg}`}>p. {pg}</PageBadge>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Key points */}
                {kpoints.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <NumaLogo size={7} className="rounded-lg" />
                      <p className="text-[11px] uppercase tracking-widest text-assistant font-medium">Punti chiave</p>
                    </div>
                    <ol className="space-y-2.5" data-testid="key-points-list">
                      {kpoints.map((k, i) => (
                        <li key={i} data-testid={`key-point-${i}`} className="bg-surface border border-line rounded-md p-4">
                          <div className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full bg-assistant-soft text-assistant text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-ink leading-relaxed">{k.text}</p>
                              <QuoteBlock quote={k.quote} verse={k.verse} />
                              <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                                {k.page && <PageBadge testid={`kp-${i}-page-${k.page}`}>p. {k.page}</PageBadge>}
                                {!k.page && k.verse && <VerseBadge verse={k.verse} />}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}

                {/* Fallback if backend returned only legacy fields */}
                {blocks.length === 0 && kpoints.length === 0 && summary?.summary && (
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-assistant font-medium mb-2">Sintesi</p>
                    <p className="text-sm leading-relaxed text-ink whitespace-pre-wrap">{summary.summary}</p>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          <div className="flex justify-between pt-3 border-t border-line">
            <Button
              data-testid="resummarize-btn"
              variant="outline"
              onClick={() => summarize(openSummary)}
              disabled={busyId === openSummary?.pdf_id}
              className="rounded-md border-line"
            >
              <BookOpen className="w-3.5 h-3.5 mr-2" /> Rigenera
            </Button>
            <Button onClick={() => { setOpenSummary(null); setSummary(null); }} className="rounded-md bg-brand hover:bg-brand-hover">
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Page viewer dialog */}
      <Dialog open={pageViewer.open} onOpenChange={(o) => !o && setPageViewer({ open: false, pdf: null, page: 1, pages: [], loading: false })}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl rounded-md">
          <DialogHeader>
            <DialogTitle className="font-serif-display">Anteprima pagina</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {pageViewer.loading ? (
              <div className="py-8 text-center">Caricamento pagina...</div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium">{pageViewer.pdf?.filename}</div>
                  <div className="text-xs text-ink-muted">Pagina {pageViewer.page} / {pageViewer.pages.length}</div>
                </div>
                <div className="bg-surface border border-line rounded-md p-4 text-sm whitespace-pre-wrap max-h-[60vh] overflow-y-auto">
                  {pageViewer.pages[pageViewer.page - 1] || "(Pagina vuota)"}
                </div>
                <div className="flex justify-between mt-3">
                    <div>
                      <Button variant="outline" onClick={() => setPageViewer((s) => ({ ...s, page: Math.max(1, s.page - 1) }))} className="rounded-md">Prec</Button>
                      <Button variant="outline" onClick={() => setPageViewer((s) => ({ ...s, page: Math.min((s.pages || []).length, s.page + 1) }))} className="rounded-md ml-2">Succ</Button>
                    </div>
                  <Button onClick={() => setPageViewer({ open: false, pdf: null, page: 1, pages: [], loading: false })} className="rounded-md bg-brand text-on-solid">Chiudi</Button>
                </div>
              </div>
            )}
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Domande sul testo</p>
              <div className="flex gap-2">
                <input value={qaState.question} onChange={(e) => setQaState((s) => ({ ...s, question: e.target.value }))} placeholder="Fai una domanda su questo documento..." className="flex-1 rounded-md border px-3 py-2" />
                <Button disabled={qaState.loading || !qaState.question.trim()} onClick={async () => {
                  setQaState((s) => ({ ...s, loading: true, answer: null }));
                  try {
                    const res = await pdfsApi.qa(pageViewer.pdf.pdf_id, qaState.question.trim());
                    setQaState((s) => ({ ...s, loading: false, answer: res }));
                  } catch (e) {
                    toast.error('Errore nella risposta');
                    setQaState((s) => ({ ...s, loading: false }));
                  }
                }} className="rounded-md bg-brand text-on-solid">Chiedi</Button>
              </div>
              {qaState.answer && (
                <div className="mt-3 bg-surface border border-line rounded-md p-3 text-sm">
                  <p className="font-medium">Risposta</p>
                  <p className="mt-2 text-ink">{qaState.answer.answer}</p>
                  {qaState.answer.citations?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-ink-muted mb-1">Citazioni</p>
                      <div className="flex flex-col gap-2">
                        {qaState.answer.citations.map((c, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <button className="text-xs text-assistant underline" onClick={() => openPageViewer(pageViewer.pdf, c.page)}>
                              p. {c.page}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Elimina fonte</AlertDialogTitle>
              <AlertDialogDescription>
                Sei sicuro di voler eliminare "{confirmDelete?.filename}"? Questa azione non può essere annullata.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-md">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} className="bg-danger text-on-solid hover:bg-danger/90">Elimina</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }
